import { SmallSystemIconType } from '@pensionbee/honeypot-ui';

export type BetaFeatureKey = string;

type BetaFeatureLabsConfig = {
  title: string;
  icon: SmallSystemIconType;
  description?: string;
  switchLabel: string;
  testId: string;
};

type BetaFeatureConfigBase = {
  key: BetaFeatureKey;
  eligibleFlags: string[];
  defaultEnabled: boolean;
  showPreviewIndicatorWhenEnabled: boolean;
};

type OptInBetaFeatureConfig = BetaFeatureConfigBase & {
  mode: 'optIn';
  labs: BetaFeatureLabsConfig;
};

type ForcedBetaFeatureConfig = BetaFeatureConfigBase & {
  mode: 'forced';
  labs?: never;
};

type BetaFeatureConfig = OptInBetaFeatureConfig | ForcedBetaFeatureConfig;

// Example of how to register a beta feature that is toggled via the Labs opt-in UI.
//
// export const betaFeaturesConfig: Record<BetaFeatureKey, BetaFeatureConfig> = {
//   mwrChart: {
//     key: 'mwrChart',
//     mode: 'optIn',
//     eligibleFlags: ['mwrChart'],
//     defaultEnabled: false,
//     showPreviewIndicatorWhenEnabled: true,
//     labs: {
//       title: 'New performance chart',
//       icon: 'Star',
//       switchLabel:
//         'Enable the personal rate of return (MWR/IRR) chart and performance figures in the My Pension & Summary tabs',
//       testId: 'mwr-opt-in-toggle',
//     },
//   },
// };

export const betaFeaturesConfig: Record<BetaFeatureKey, BetaFeatureConfig> = {};

const betaFeatureKeys = Object.keys(betaFeaturesConfig) as BetaFeatureKey[];

export const isBetaFeatureEligibleByFlags = (
  key: BetaFeatureKey,
  hasFlag: (flagName: string) => boolean,
): boolean => {
  return betaFeaturesConfig[key].eligibleFlags.some((flagName) =>
    hasFlag(flagName),
  );
};

export const getEligibleBetaFeatureKeysByFlags = (
  hasFlag: (flagName: string) => boolean,
): BetaFeatureKey[] => {
  return betaFeatureKeys.filter((key) =>
    isBetaFeatureEligibleByFlags(key, hasFlag),
  );
};

export const getOptInBetaFeatureKeysByFlags = (
  hasFlag: (flagName: string) => boolean,
): BetaFeatureKey[] => {
  return getEligibleBetaFeatureKeysByFlags(hasFlag).filter(
    (key) => betaFeaturesConfig[key].mode === 'optIn',
  );
};
